import React, { useEffect, useMemo, useState } from 'react'
import { PageSection, Title } from '@patternfly/react-core'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'design-react-kit'
import { GenericAsyncTableData, RequestData } from 'src/backoffice/app/components/GenericAsyncTableData'
import { TRIENNI_OPTIONS } from './CreaProroga'
import { getDefaultAxiosConfig, getFormazioneResourceApi } from 'src/app/utils/ManagerRestGateway'
import { TokenStore } from 'src/keycloak/jwt/TokenStore'
import { getAllEnti, TIPO_ENTE } from 'src/model/EnteFormazione'
import { generateRequestFilter, generateRequestSort } from 'src/app/utils/ApiUtils'
import { PATH_TO_FO_RIEPILOGO_RIPARTIZIONI } from 'src/app/utils/RoutesConstants'

const Spacer = () => <br />

const getTriennioLabel = (value: any) => TRIENNI_OPTIONS.find((t) => t.value === value)?.label || value

const formatDate = (date?: string) => {
  if (!date) return '-'

  const [year, month, day] = date.split('-')
  return `${day}-${month}-${year}`
}

const ListaProrogheIscritti = (props: any) => {
  const [enti, setEnti] = useState<any>({})

  useEffect(() => {
    const load = async () => {
      const entiResponse = await getAllEnti()
      setEnti(entiResponse)
    }

    load()
  }, [])

  const ordiniOptions = useMemo(() => {
    return Object.keys(enti)
      .filter((code) => enti[code]?.tipo === TIPO_ENTE.ORDINE)
      .map((code) => ({
        label: enti[code].nome,
        value: code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [enti])

  const loadData = async (request: RequestData) => {
    const { data } = await getFormazioneResourceApi().msfoFormazioneDerogheByFilterGet(
      generateRequestFilter(request.filters),
      request.pageSize,
      request.currentPage,
      generateRequestSort(request.sortColumn, request.sortDirection),
      getDefaultAxiosConfig(),
    )
    return {
      items: data.items || [],
      total: data.count || 0,
    }
  }

  const filters = useMemo(
    () => [
      { label: 'Nome', field: 'nome', type: 'input', operator: 'LIKE' },
      { label: 'Cognome', field: 'cognome', type: 'input', operator: 'LIKE' },
      { label: 'Codice Fiscale', field: 'cf', type: 'input', operator: 'LIKE' },
      {
        label: 'Triennio destinazione',
        field: 'triennio',
        type: 'select',
        options: TRIENNI_OPTIONS,
      },

      ...(TokenStore.getInstance().isCurrentAccountCONAF()
        ? [
            {
              label: 'Ordine di appartenenza',
              field: 'ordine_territoriale_competente',
              type: 'select',
              options: ordiniOptions,
            },
          ]
        : []),
    ],
    [ordiniOptions],
  )

  const riepilogoRipartizioni = (id: number, cf?: string) => {
    props.history.push(`${PATH_TO_FO_RIEPILOGO_RIPARTIZIONI}?id=${id}&cf=${cf}`)
  }

  const renderActions = (row: any) => {
    return (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button color="primary" size="sm" onClick={() => riepilogoRipartizioni(row.id, row?.cf)}>
          Visualizza ripartizioni
        </Button>
      </div>
    )
  }

  return (
    <PageSection>
      <div className="container register-container">
        <Spacer />

        <Title headingLevel="h1" size="lg">
          Lista proroghe
        </Title>

        <Spacer />

        <GenericAsyncTableData
          tableKey="lista-proroghe-iscritti"
          emptyMessage="Nessuna proroga presente"
          getRows={loadData}
          filters={filters}
          columns={[
            {
              label: 'Nome',
              field: 'nome',
              isSortable: true,
            },
            {
              label: 'Cognome',
              field: 'cognome',
              isSortable: true,
            },
            {
              label: 'Codice Fiscale',
              field: 'cf',
              isSortable: true,
            },
            {
              label: 'Ordine Territoriale',
              field: 'ordine_territoriale_competente',
              isSortable: false,
            },
            {
              label: 'Triennio Destinazione',
              field: 'triennio',
              isSortable: false,
              render: (row: any) => getTriennioLabel(row.triennio),
            },
            {
              label: 'Inizio Proroga',
              field: 'dataInizioDeroga',
              isSortable: false,
              render: (row: any) => formatDate(row.dataInizioDeroga),
            },
            {
              label: 'Fine Proroga',
              field: 'dataFineDeroga',
              isSortable: false,
              render: (row: any) => formatDate(row.dataFineDeroga),
            },
            {
              label: 'Data Inserimento',
              field: 'dataInserimento',
              isSortable: false,
              render: (row: any) => formatDate(row.dataInserimento.split('T')[0]),
            },
            {
              label: 'Provvedimento Di Concessione',
              field: 'provvedimento',
              isSortable: false,
            },
            {
              label: 'Crediti Spostati',
              field: 'creditiSpostati',
              isSortable: false,
              render: (row) => Number(row.creditiSpostati || 0).toFixed(3),
            },
            {
              label: '',
              field: 'actions',
              isSortable: false,
              render: renderActions,
            },
          ]}
        />
      </div>
    </PageSection>
  )
}

export default ListaProrogheIscritti
